import {
  RenderAnswerGroup,
  RenderAnswerGroupAsSubquestion,
} from "../helpers";
import Significance from "../Significance/Significance";
import BMISection from "../BMISection/BMISection";
import { Fragment, useState, useRef, useEffect } from "react";
import PhotoDiagnose from "../PhotoDiagnose/PhotoDiagnose";
import PhotoSelectionPanel from "../PhotoSelectionPanel/PhotoSelectionPanel";
import UnitToggle from "../ImperialInput/UnitToggle";
import { IMPERIAL } from "../ImperialInput/imperialUtils";
import { useParams } from "react-router-dom";

/*
this component is responsible for the following:
1. rendering the content of a question, based on it's type
2. rendering the  immediate set of answers for this question (as an answerGroup)
3. rendering significances of a question , if the corresponding answer was selected
4. render all the additional subquestions ONLY IF their parent answer was selected, (that information is inside formData)

 */

const Question = ({
  handleRef,
  question,
  onAnswerClick,
  formData,
  onTextChange,
  onReveal,
  onHide,
  questionnaireValidationObject,
  files = [],
  setFiles,
  dangerSignificancesRefsDispatch,
  answersSelectedMap,
  mention,
  disabled,
  flattenedQuestionnaire = [],
  bmiValidationFailed = false,
  uploadValidationFailed = false,
  
}: {
  handleRef: any
  question: any
  onAnswerClick: any
  formData: any
  onTextChange: any
  onReveal: any
  onHide: any
  questionnaireValidationObject: any
  files: Array<any>
  setFiles: any
  dangerSignificancesRefsDispatch:(action:"add" | "remove", ref:any) => void
  answersSelectedMap : any,
  mention:string | undefined
  disabled?:boolean
  flattenedQuestionnaire?: any[]
  bmiValidationFailed?: boolean
  uploadValidationFailed?: boolean
  
}) => {
  const [photoSelectionPickedIdx, setPhotoSelectionPickedIdx] = useState(null);
  const [isImperial, setIsImperial] = useState(false);
  let { category, language } = useParams();
  language = language || "de"

  const ref = useRef() as React.MutableRefObject<HTMLInputElement>;
  useEffect(() => {
    handleRef(question.id, ref);

    question.additional?.forEach((subquestion: any) => {
      handleRef(subquestion.recipe.id, ref);
    });
  }, []);

  function getQuestionBasedOnType(question: any) {

    switch (question.question_type) {
      case "title":
        return (
          <h2 className="questionnaire-questions-group-headline">
            {question.text}
          </h2>
        );

      case "photo_diagnose":
        return (
          <>
            <div
              id={"question_" + question.id}
              className="questionnaire-question-item-container"
              ref={ref}
            >
              <div className="questionnaire-question-item-column">
                {/* step 1: rendering the contents of the question (based on type) */}
                <div className="questionnaire-text-and-helpblock-container">
                  <div
                    className="questionnaire-question-item-text-container"
                    dangerouslySetInnerHTML={{ __html: question.text }}
                  />
                  <div
                    className="questionnaire-question-item-helpblock-container"
                    dangerouslySetInnerHTML={{ __html: question.help_block }}
                  />
                </div>

                <div className="questionnaire-answers-container">
                  {/* 
                  case 1: Answer group is also photo_diagnose (question.answersGroup?.recipe?.type == "photo_diagnose"):
                  The upload widget is rendered by RenderAnswerGroup, which handles photo_diagnose answer groups. 
                  Rendering <PhotoDiagnose> here would be a duplicate upload widget
                  
                  case 2: Answer group is something else (question.answersGroup?.recipe?.type != "photo_diagnose"):
                  The question is a photo_diagnose type but the answer group handles other input. 
                  The upload widget is not provided by RenderAnswerGroup, so <PhotoDiagnose> 
                  needs to render here to give the customer the upload option
                  */} 
                  {question.answersGroup?.recipe?.type !== "photo_diagnose" && question.answersGroup?.recipe?.type !== "document_upload" && (
                    <PhotoDiagnose
                      language={language}
                      category={category || mention}
                      files={files}
                      setFilesCallback={setFiles}
                      showError={uploadValidationFailed}
                    />
                  )}
                  <RenderAnswerGroup
                    disabled={disabled}
                    questionnaireValidationObject={
                      questionnaireValidationObject
                    }
                    onAnswerClick={onAnswerClick}
                    onTextChange={onTextChange}
                    answersGroup={question.answersGroup}
                    onReveal={onReveal}
                    onHide={onHide}
                    answersSelectedMap={answersSelectedMap}
                    files={files}
                    setFiles={setFiles}
                    language={language}
                    category={category || mention}
                    showUploadError={uploadValidationFailed}
                  />
                </div>
              </div>
            </div>
          </>
        );

      case "photo_selection": {
        const prefilledPickedIdx = question.answersGroup?.children?.findIndex(
          (child: any) =>
            answersSelectedMap[child.recipe.id] ||
            answersSelectedMap[child.recipe.id] === 0
        );

        return (
          <>
            <PhotoSelectionPanel
              containerRef={ref}
              errorMode={
                questionnaireValidationObject[
                question.answersGroup.recipe.id
                ] === false
              }
              question={question}
              photoSelectionPickedIdx={
                photoSelectionPickedIdx ??
                (prefilledPickedIdx !== undefined && prefilledPickedIdx >= 0
                  ? prefilledPickedIdx
                  : null)
              }
              onPhotoChange={(answer: any, idx: any) => {
                setPhotoSelectionPickedIdx(idx);
                onAnswerClick(answer, idx);
              }}
            />
            <RenderAnswerGroup
              disabled={disabled}
              questionnaireValidationObject={questionnaireValidationObject}
              onAnswerClick={onAnswerClick}
              onTextChange={onTextChange}
              answersGroup={question.answersGroup}
              onReveal={onReveal}
              onHide={onHide}
              answersSelectedMap={answersSelectedMap}
              files={files}
              setFiles={setFiles}
              language={language}
              category={category || mention}
              showUploadError={uploadValidationFailed}
            />
          </>
        );
      }

      case "bmi_question":
        return (
          <BMISection
            question={question}
            handleRef={handleRef}
            onAnswerClick={onAnswerClick}
            formData={formData}
            onTextChange={onTextChange}
            onReveal={onReveal}
            onHide={onHide}
            questionnaireValidationObject={questionnaireValidationObject}
            files={files}
            setFiles={setFiles}
            dangerSignificancesRefsDispatch={dangerSignificancesRefsDispatch}
            answersSelectedMap={answersSelectedMap}
            mention={mention}
            disabled={disabled}
            language={language}
            bmiValidationFailed={bmiValidationFailed}
            flattenedQuestionnaire={flattenedQuestionnaire}
          />
        );

      default: {
        const rule = question.answersGroup?.recipe?.validation_rule;
        const imperialHeight = !!rule?.allow_imperial_height;
        const imperialWeight = !!rule?.allow_imperial_weight;
        const hasImperial = imperialHeight || imperialWeight;
        const metricLabel = imperialHeight ? IMPERIAL.labels.cm : IMPERIAL.labels.kg;
        const imperialLabel = imperialHeight ? IMPERIAL.labels.ftIn : IMPERIAL.labels.stLb;

        return (
          <>
            <div
              id={"question_" + question.id}
              className="questionnaire-question-item-container"
              ref={ref}
            >
              <div
                className={
                  question.answersGroup?.recipe?.type === "check_list" ||
                    question.answersGroup?.recipe?.type === "textarea"
                    ? "questionnaire-question-item-column"
                    : "questionnaire-question-item"
                }
              >
                <div className="questionnaire-text-and-helpblock-container">
                  <div className={`questionnaire-question-item-text-container${hasImperial ? " unit-toggle-text-row" : ""}`}>
                    <span dangerouslySetInnerHTML={{ __html: question.text }} />
                    {hasImperial && (
                      <UnitToggle
                        isImperial={isImperial}
                        onToggle={setIsImperial}
                        metricLabel={metricLabel}
                        imperialLabel={imperialLabel}
                      />
                    )}
                  </div>
                  <div
                    className="questionnaire-question-item-helpblock-container"
                    dangerouslySetInnerHTML={{ __html: question.help_block }}
                  />
                </div>

                <div className="questionnaire-answers-container">
                  <RenderAnswerGroup
                    disabled={disabled}
                    questionnaireValidationObject={
                      questionnaireValidationObject
                    }
                    onTextChange={onTextChange}
                    onAnswerClick={onAnswerClick}
                    answersGroup={question.answersGroup}
                    onReveal={onReveal}
                    onHide={onHide}
                    answersSelectedMap={answersSelectedMap}
                    isImperial={isImperial}
                    files={files}
                    setFiles={setFiles}
                    language={language}
                    category={category || mention}
                    showUploadError={uploadValidationFailed}
                  />
                </div>
              </div>

              {/* step 3: rendering the significances */}
              {question.answersGroup?.children?.flatMap((childAnswer: any) => {
                if (
                  !formData[childAnswer.recipe?.id] ||
                  !childAnswer.recipe.significances?.length
                ) {
                  return [];
                }
                // `normal` is a doctor-only level (no patient-facing styling);
                // filter so it cannot mask the visible notes that follow it.
                return childAnswer.recipe.significances
                  .filter((sig: any) => sig && sig.level && sig.level !== 'normal')
                  .map((sig: any, sigIdx: number) => (
                    <Significance
                      parentID={childAnswer.recipe.id}
                      key={`${childAnswer.recipe.id}-${sig.recipe_significance_id ?? sigIdx}`}
                      fontWeight
                      level={sig.level}
                      text={sig.title}
                      dangerSignificancesRefsDispatch={dangerSignificancesRefsDispatch}
                    />
                  ));
              })}

              {/* step 3b: per-option help blocks (visible only for picked sub-answers) */}
              {question.answersGroup?.children?.map((childAnswer: any) => {
                if (
                  !formData[childAnswer.recipe?.id] ||
                  !childAnswer.recipe?.help_block
                ) {
                  return null;
                }
                return (
                  <div
                    key={`hb-${childAnswer.recipe.id}`}
                    className="questionnaire-question-item-helpblock-container"
                    dangerouslySetInnerHTML={{ __html: childAnswer.recipe.help_block }}
                  />
                );
              })}
            </div>

            {/* step 4: rendering additional (subquestions) if their parent is selected */}
            <div className="questionnaire-subquestion-container">
              {question.additional?.map((additonalQuestionGroup: any, idx: number) => {
                if (
                  additonalQuestionGroup.recipe.parent_id &&
                  formData[additonalQuestionGroup.recipe.parent_id]
                ) {
                  return (
                    <Fragment key={additonalQuestionGroup.recipe.id}>
                      <RenderAnswerGroupAsSubquestion
                        disabled={disabled}
                        questionnaireValidationObject={
                          questionnaireValidationObject
                        }
                        key={idx}
                        answersGroup={additonalQuestionGroup}
                        onAnswerClick={onAnswerClick}
                        onTextChange={onTextChange}
                        onReveal={onReveal}
                        onHide={onHide}
                        answersSelectedMap={answersSelectedMap}
                        files={files}
                        setFiles={setFiles}
                        language={language}
                        category={category || mention}
                        showUploadError={uploadValidationFailed}
                      />

                      {additonalQuestionGroup.children.flatMap((child: any) => {
                        if (
                          !formData[child.recipe.id] ||
                          !child.recipe.significances?.length
                        ) {
                          return [];
                        }
                        return child.recipe.significances
                          .filter((sig: any) => sig && sig.level && sig.level !== 'normal')
                          .map((sig: any, sigIdx: number) => (
                            <Significance
                              parentID={child.recipe.id}
                              key={`${child.recipe.id}-${sig.recipe_significance_id ?? sigIdx}`}
                              fontWeight
                              level={sig.level}
                              text={sig.title}
                              dangerSignificancesRefsDispatch={dangerSignificancesRefsDispatch}
                            />
                          ));
                      })}

                      {/* per-option help blocks for sub-question children (visible only when picked) */}
                      {additonalQuestionGroup.children.map((child: any) => {
                        if (
                          !formData[child.recipe?.id] ||
                          !child.recipe?.help_block
                        ) {
                          return null;
                        }
                        return (
                          <div
                            key={`hb-${child.recipe.id}`}
                            className="questionnaire-question-item-helpblock-container"
                            dangerouslySetInnerHTML={{ __html: child.recipe.help_block }}
                          />
                        );
                      })}
                    </Fragment>
                  );
                } else {
                  return null;
                }
              })}
            </div>
          </>
        );
      }
    }
  }
  return <>{getQuestionBasedOnType(question)}</>;
};

export default Question;
